import { useCurrency, charm, formatAmount } from "@/contexts/CurrencyContext";

/**
 * <Price usd={19.99} />   or   <Price gbp={15.99} />
 *
 * Renders the visitor's local presentment price, charm-rounded. While the
 * currency context is still loading we show a transparent skeleton so layout
 * never jumps. The component intentionally never shows the source currency —
 * per brand requirement "only their currency, not both".
 */
export default function Price({ usd, gbp, className = "", prefix = "", suffix = "" }) {
  const { currency, symbol, fxUsd, decimals, ready, usdToGbp } = useCurrency();

  if (!ready) {
    return <span className={`inline-block bg-white/5 rounded w-16 h-[1em] align-middle ${className}`} aria-busy="true" />;
  }

  // Convert the supplied source amount → USD first (our base), then → target.
  let usdAmount;
  if (typeof usd === "number" && isFinite(usd)) {
    usdAmount = usd;
  } else if (typeof gbp === "number" && isFinite(gbp)) {
    // GBP → USD using the live rate from the currency context. Falls back to
    // ~0.79 if the rate hasn't loaded yet (charm rounding absorbs drift).
    const rate = usdToGbp || 0.79;
    usdAmount = gbp / rate;
  } else {
    return null;
  }

  const targetAmount = charm(usdAmount * fxUsd, decimals);
  const formatted = formatAmount(targetAmount, decimals);

  return (
    <span className={className} data-testid={`price-${currency.toLowerCase()}`}>
      {prefix}
      <span className="font-['JetBrains_Mono']">{symbol}</span>
      {formatted}
      {suffix}
    </span>
  );
}
